/**
 * Plan gating — the frontend's single source of truth for "which plan
 * unlocks which feature" LABELS (badges, lock hints).
 *
 * Mirrors backend/plan-quotas.config.js TIERS. The backend config is
 * authoritative for ENFORCEMENT (every route/job checks it server-side);
 * this file only drives display strings, and must be updated in the
 * same commit whenever a gate or quota moves between tiers there.
 *
 * "Lowest tier" logic mirrors the backend exactly:
 *   - gated features  → the cheapest tier whose gates.<feature> is true
 *   - metered features → the cheapest tier whose quota is > 0
 */

export const TIER_ORDER = ['starter', 'growth', 'scale', 'enterprise'];

export const TIER_NAMES = {
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
  enterprise: 'Enterprise',
};

/**
 * feature → the lowest tier where it's available.
 *
 * Sources (backend/plan-quotas.config.js):
 *   productBuilds   quota:  starter 2      → starter
 *   websites        no hard cap — builds draw the changes budget,
 *                   present on every tier  → starter
 *   imageAds        quota:  starter 10     → starter
 *   advertorials    quota:  starter 3      → starter
 *   findProducts    quota:  starter 3 (findProductsSearches) → starter
 *   adCopy          quota:  starter 1      → starter
 *   upsells         gate:   growth         → growth
 *   publishAds      gate:   growth         → growth
 *   mobileApp       gate:   growth         → growth
 *   portraitPlacements gate: growth        → growth
 *   videoAds        gate:   scale          → scale
 *   splitTests      gate:   scale          → scale
 *   aiChat          gate:   scale          → scale
 *
 * Dashboard-only informational cards (no config gate of their own —
 * both surface insights derived from PUBLISHED ad performance, so they
 * follow the publishAds gate):
 *   scalingSuggestions  → growth
 *   winningAdDetection  → growth
 */
export const FEATURE_MIN_TIER = {
  productBuilds: 'starter',
  websites: 'starter',
  imageAds: 'starter',
  advertorials: 'starter',
  findProducts: 'starter',
  adCopy: 'starter',
  upsells: 'growth',
  publishAds: 'growth',
  mobileApp: 'growth',
  portraitPlacements: 'growth',
  scalingSuggestions: 'growth',
  winningAdDetection: 'growth',
  videoAds: 'scale',
  splitTests: 'scale',
  aiChat: 'scale',
};

/**
 * The display NAME of the lowest tier that has `feature` — "Starter",
 * "Growth", "Scale" … Badge callers add their own suffix convention
 * (the Dashboard renders `${label}+`). Unknown feature → null.
 */
export function requiredPlanLabel(feature) {
  const tier = FEATURE_MIN_TIER[feature];
  return tier ? TIER_NAMES[tier] : null;
}
